import { useMemo } from 'react';
import { BookingStatisticFiltered } from '../../types/booking-statistic-filtered';
import { StatisticsVariant } from '../../types/statistic-variant';
import {
  axisClasses,
  ChartDataProvider,
  ChartsGrid,
  chartsGridClasses,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LineHighlightPlot,
  LinePlot,
} from '@mui/x-charts';
import { legendClasses } from '@mui/x-charts/ChartsLegend';
import { Typography } from '@mui/material';
import { prepareLineChartStatisticsData } from '../../utils/chart';
import { useTranslation } from 'react-i18next';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { ContentType } from '../../types/content-type';

type Props = {
  data: BookingStatisticFiltered[];
  isLoading: boolean;
  error: string | undefined;
  variant: StatisticsVariant;
  contentType: ContentType;
};

export default function StatisticsLineChart({
  data,
  isLoading,
  error,
  variant,
  contentType,
}: Props) {
  const { t } = useTranslation(['statistics', 'common']);

  const { xAxis, series } = useMemo(
    () => prepareLineChartStatisticsData(data, variant, contentType, t),
    [data, variant, contentType, t],
  );

  return (
    <>
      {data?.length ? (
        <>
          <Typography sx={{ mx: 2.5, color: '#A0A19B', fontSize: '10px' }}>
            {t('common.chart.yAxisLabel')}
          </Typography>
          <ChartDataProvider
            margin={{ right: 50 }}
            series={series}
            height={300}
            yAxis={[
              {
                min: 0,
              },
            ]}
            xAxis={xAxis}>
            <ChartsSurface
              sx={{
                //grid lines
                [`& .${chartsGridClasses.line}`]: {
                  strokeWidth: 1,
                  color: '#F0F0F0',
                  opacity: '35%',
                },

                //y axis
                [`.${axisClasses.directionY}`]: {
                  transform: 'translate(56px, 0)',
                },
                [`.${axisClasses.directionX}`]: {
                  transform: 'translate(0, 260px)',
                },
              }}>
              <ChartsGrid horizontal vertical />
              <ChartsXAxis
                disableLine
                disableTicks
                tickLabelStyle={{
                  fill: '#A0A19B',
                  fontSize: '10px',
                }}
              />
              <ChartsYAxis
                disableTicks
                disableLine
                tickLabelStyle={{
                  fill: '#A0A19B',
                  fontSize: '10px',
                }}
              />
              <LinePlot />
              <LineHighlightPlot />
              <ChartsTooltip />
            </ChartsSurface>
            <ChartsLegend
              sx={{
                justifyContent: 'center',
                gap: 2,
                marginY: 3,
                [`${legendClasses.series}`]: { gap: 1 },
                [`.${legendClasses.mark}`]: {
                  height: '8px !important',
                  marginRight: -0.5,
                },
              }}
            />
          </ChartDataProvider>{' '}
        </>
      ) : null}
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </>
  );
}
